<template>
  <div class="navigation">
    <nav class="navigation__nav">
      <ul class="navigation__list">
        <li
          v-for="(tier1, index) of desktop.items"
          :id="getParentKey(tier1, index)"
          :key="getParentKey(tier1, index)"
          class="navigation__item"
          v-on="getParentEvents(tier1, index)"
        >
          <template v-if="itemHasChildren(tier1)">
            <a
              class="navigation__link"
              :class="[
                $class('disabled'),
                {
                  [$class('active')]: parentHover === getParentKey(tier1, index),
                }
              ]"
              type="button"
              :style="getLinkStyle(tier1.title)"
              :href="tier1.url"
            >
              <h2
                class="text-body-m-regular nav-link-font"
                v-html="tier1.title"
              />
            </a>

            <btn
              class="accessibility__sign-post"
              type="button"
              modifiers="text"
              :label="false"
              :aria-label=" $string('general.site_header.show_hide_tier', {
                replace: {
                  action: parentHover ===
                    getParentKey(tier1, index) ? 'Hide' : 'Show',
                  title: sanitizeTitle(tier1.title)
                }
              })"
              :aria-expanded="parentHover === getParentKey(tier1, index)"
              :style="getLinkStyle(tier1.title)"
              show-icon
              @keydown.enter.prevent="toggleParentHover(getParentKey(tier1, index))"
              @keydown.space.prevent="toggleParentHover(getParentKey(tier1, index))"
              @click.prevent="toggleParentHover(getParentKey(tier1, index))"
            />

            <div
              v-if="itemHasChildren(tier1)"
              class="site-header__mega-nav"
            >
              <mega-nav
                v-show="parentHover === getParentKey(tier1, index)"
                :tier-title="tier1.title"
                :items="tier1.items"
                :image="tier1.image"
                :scribble="getScribble(tier1.title, index)"
                :signpost="getSignpost(tier1.title)"
                :highlights="getHighlights(tier1.title)"
                :nav-link-colors="getNavLinkColors(tier1.title)"
              />
            </div>
          </template>

          <template v-else>
            <a
              class="navigation__link text-body-m-regular"
              :href="tier1.url"
              :style="getLinkStyle(tier1.title)"
              v-html="tier1.title"
            />
          </template>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>

/**
 * Vue: Navigation (navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Array} scribbleExclusions - Panels that shouldn't render a scribble.
 * @param {Array} signposts - Featured image and link for mega nav panels.
 * @param {Array} highlights - Featured images and links for mega nav panels.
 * @param {Array} navLinkColors - Links that have been assigned colors.
 *
 */
import Btn from '~global/btn/btn'
import MegaNav from '../mega-nav/mega-nav.vue'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    Btn,
    MegaNav,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    scribbleExclusions: {
      type: Array,
      default: () => [],
    },
    signposts: {
      type: Array,
      default: () => [],
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    navLinkColors: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      parentHover: false,
    }
  },

  methods: {

    /**
     * Get signpost by link title.
     * @param {String} title - Tier 1 link title.
     * @returns {Boolean|Object}
     */
    getSignpost(title) {
      return this.signposts.find((signpost) => {
        return signpost.key.toLowerCase() === this.sanitizeTitle(title)
      }) || false
    },

    /**
     * Get highlights by link title.
     * @param {String} title - Tier 1 link title.
     * @returns {Boolean|Array}
     */
    getHighlights(title) {
      const matchedObject = this.highlights.find((highlights) => {
        return highlights.keys.find(key => {
          return key.toLowerCase() === this.sanitizeTitle(title)
        })
      })

      return matchedObject?.highlights || false
    },

    /**
     * Sanitize title to remove any HTML and prevent case-sensitivity.
     * @param {String} title - Tier 1 link title.
     * @returns {String}
     */
    sanitizeTitle(title) {
      const regex = new RegExp(/<[^>]*>(?<inner>[^<]*)<\/[^>]*>/, 'gu')

      return title.replace(regex, (match, group) => group).toLowerCase()
    },

    /**
     * Get which scribble line to render, if any.
     * Exclusions are set via a section setting.
     * @param {String} title - Tier 1 link title.
     * @param {Number} index - Tier 1 index.
     * @returns {Number|Boolean}
     */
    getScribble(title, index) {
      if (this.scribbleExclusions.find(exclusion => {
        return exclusion.toLowerCase() === this.sanitizeTitle(title)
      })) {
        return false
      }

      if (index < 3) {
        return index + 1
      }

      return (Math.abs(index - this.scribbleExclusions.length) % 3) + 1
    },

    /**
     * Returns if the given item has children items.
     * @param {Object} item - The item to test.
     * @returns {Boolean}
     */
    itemHasChildren(item) {
      return item.items && item.items.length
    },

    /**
     * Returns the key for a parent item.
     * @param {Object} item - The menu item object.
     * @param {Number} index - The item index.
     * @returns {String}
     */
    getParentKey(item, index) {
      return `tier1-${index}-${item.handle}`
    },

    /**
     * Returns the events for the parent menu item.
     * @param {Object} item - The menu item object.
     * @param {Number} index - The item index.
     * @returns {Object}
     */
    getParentEvents(item, index) {
      const mouseHandler =
        (event) => this.parentMouseEvent(event, this.getParentKey(item, index))

      if (!this.itemHasChildren(item)) {
        return {}
      }

      return {
        mouseenter: mouseHandler,
        mouseleave: mouseHandler,
      }
    },

    /**
     * Get style attribute with color for tier 1 links.
     * @param {String} title - Tier 1 link title.
     * @returns {String}
     */
    getLinkStyle(title) {
      const titleToMatch = this.sanitizeTitle(title).toLowerCase()

      const matchedColor = this.navLinkColors.find(color => {
        return color.toLowerCase().split(':')[0] === titleToMatch
      })

      return matchedColor ? `color: ${matchedColor.split(':')[1]}` : ''
    },

    /**
     * Get link colors to pass into mega nav for tiers 2 and 3.
     * @param {String} title - Tier 1 link title.
     * @returns {Array}
     */
    getNavLinkColors(title) {
      const titleToMatch = this.sanitizeTitle(title).toLowerCase()

      const matchedColors = this.navLinkColors.filter(color => {
        return color.toLowerCase().startsWith(`${titleToMatch}|`)
      })

      const formattedMatchedColors = matchedColors.map(color => {
        return color.toLowerCase().split(`${titleToMatch}|`)[1]
      })

      return formattedMatchedColors
    },

    /**
     * Handles the parent mouse enter event.
     * @param {Object} event - The event object.
     * @param {String} key - The parent key.
     */
    parentMouseEvent(event, key) {
      if (event.type === 'mouseenter') {
        this.parentHover = key
        return
      }

      this.parentHover = false
    },

    /**
     * Toggles the parent hover based on a key.
     * @param {String} key - The parent key.
     */
    toggleParentHover(key) {
      if (this.parentHover === key) {
        this.parentHover = false
        return
      }

      this.parentHover = key
    },
  },
}
</script>
